import React, { useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Sparkles, Wrench, AlertTriangle, CheckCircle2, Loader2, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Catalog of available tune generators. Each entry knows its endpoint and the
 * one-line "best for" hint. Order = recency / typical workflow priority.
 */
const GENERATORS = [
  {
    id: 'v27-oem-e85-78psi-short',
    endpoint: '/api/tunes/generate/v27-oem-e85-78psi-short',
    name: 'V27-Short — OEM + 78 PSI + E85 (Short Flash)',
    description: 'Uniform 1.208× fuel scaling for OEM injectors + NEW 78 PSI external regulator + E85. Fixes the catastrophic lean seen on Jul 1 datalog (WOT AFR 19+ vs 14.6 target). Knock threshold stays stock (tight sensors handle it). Short Flash OK.',
    bestFor: 'CURRENT HARDWARE: OEM + 78 PSI new FPR + E85',
    flashType: 'SHORT FLASH OK',
    flagKey: 'v27Short',
    bodyless: true,
  },
  {
    id: 'v27-oem-e85-78psi-full',
    endpoint: '/api/tunes/generate/v27-oem-e85-78psi-full',
    name: 'V27-Full — OEM + 78 PSI + E85 (Full Flash)',
    description: 'Same 1.208× fueling as V27-Short but adds knock threshold × 1.5 protection + misfire detection enabled. Requires Full Flash to commit knock bytes.',
    bestFor: 'Same as V27-Short but with knock protection layer',
    flashType: 'FULL FLASH REQUIRED',
    flagKey: 'v27Full',
    bodyless: true,
  },
  {
    id: 'v26-oem-e85-68psi',
    endpoint: '/api/tunes/generate/v26-oem-e85-68psi',
    name: 'V26 — OEM + 68 PSI New FPR + E85',
    description: 'For replaced fuel pump + new 68 PSI regulator + OEM injectors + E85. Load-tapered 1.293× WOT scaling (idle cells stock — no oscillation). Knock 1.5×, misfire on, DISA + cut-outs preserved. Replaces V23 (which assumed 86 PSI and runs lean at 68 PSI).',
    bestFor: 'OEM injectors + 68 PSI rail + E85 (CURRENT hardware)',
    flashType: 'FULL FLASH REQUIRED',
    flagKey: 'newFpr68psi',
    bodyless: true,
  },
  {
    id: 'v25-oem-e85-86psi-idle-fix',
    endpoint: '/api/tunes/generate/v25-oem-e85-86psi-idle-fix',
    name: 'V25 — Idle Fix (Tapered Fueling)',
    description: 'V24 idle oscillation fix. Load-axis tapered fuel scaling: idle/cruise cells stay STOCK (closed loop adapts via STFT/LTFT), only WOT cells get the full 1.15× E85 boost. Knock 1.5×, misfire ENABLED, DISA + cut-outs preserved.',
    bestFor: 'OEM injectors + 86 PSI + E85 with stable idle (fixes V24 AFR swing)',
    flashType: 'FULL FLASH REQUIRED',
    flagKey: 'tighenedFuelTaper',
    bodyless: true,
  },
  {
    id: 'v24-oem-e85-86psi-tight-sensors',
    endpoint: '/api/tunes/generate/v24-oem-e85-86psi-tight-sensors',
    name: 'V24 — OEM + E85 + 86 PSI (Tight Knock Sensors)',
    description: 'V23 fueling math (1.15× for OEM injectors + 86 PSI + E85) + tightened knock threshold (1.5× vs V23\'s 2.0×) + misfire detection re-enabled. Appropriate for properly-torqued knock sensors. DISA + cut-out logic preserved.',
    bestFor: 'OEM injectors, 86 PSI rail, E85, cut-outs, CLEAN knock sensors',
    flashType: 'FULL FLASH REQUIRED',
    flagKey: 'tightSensors',
    bodyless: true,
  },
  {
    id: 'v23-oem-e85-86psi',
    endpoint: '/api/tunes/generate/v23-oem-e85-86psi',
    name: 'V23 — OEM Injectors + E85 + 86 PSI',
    description: 'Calibrated for stock OEM 13 lb/hr injectors with a failing FPR @ 86 PSI on E85. 1.15× fuel scaling (math: 1.40 E85 / √(86/58) pressure gain). Includes V22\'s knock + misfire fix. DISA + cut-out logic preserved.',
    bestFor: 'OEM injectors, 86 PSI rail, E85, cut-outs',
    flashType: 'FULL FLASH REQUIRED',
    flagKey: 'oemE85HighPressure',
    bodyless: true,
  },
  {
    id: 'v22-exhaust-knock-fix',
    endpoint: '/api/tunes/generate/v22-exhaust-knock-fix',
    name: 'V22 — Exhaust Knock Fix',
    description: 'V11 E85 baseline + raised knock threshold (2.0×). Stops exhaust cut-out resonance from false-triggering knock cuts. Preserves DISA + cut-out logic.',
    bestFor: 'Stutters at WOT with cut-outs open (Ford 47lb injectors)',
    flashType: 'FULL FLASH REQUIRED',
    flagKey: 'knockNoise',
    bodyless: true,
  },
  {
    id: 'v13-alphan',
    endpoint: '/api/tunes/generate/v13-alphan',
    name: 'V13 — Alpha-N E85',
    description: 'Switches to alpha-N fuel calc (TPS-based) for big-cam / unmetered-air builds. Best when MAF readings are unreliable.',
    bestFor: 'MAF errors, big cam, intake delete',
    flashType: 'FULL FLASH REQUIRED',
    bodyless: true,
  },
  {
    id: 'v12-safe-idle-auto',
    endpoint: '/api/tunes/generate/v12-safe-idle-auto',
    name: 'V12 Safe — Idle Auto-Lean',
    description: 'Auto-targets stoichiometric idle AFR using your last datalog\'s measured idle AFR. Safe, conservative.',
    bestFor: 'Rich idle, slow warm-up, fouled plugs',
    flashType: 'Short flash OK',
    bodyless: false,
    body: { target_afr: 14.7 },
  },
  {
    id: 'v12b-aggromid',
    endpoint: '/api/tunes/generate/v12b-aggromid',
    name: 'V12b — Aggressive Mid-Range',
    description: 'Bumps 2500–4500 RPM fueling for stronger mid-range pull. Keeps WOT cells conservative.',
    bestFor: 'Flat 3-4k RPM, weak mid-range',
    flashType: 'Short flash OK',
    bodyless: true,
  },
  {
    id: 'v11',
    endpoint: '/api/tunes/generate/v11',
    name: 'V11 — Balanced E85',
    description: 'Baseline E85 tune. Power, idle, and AFR all dialed for stock-pump or 525LPH cars.',
    bestFor: 'Reset to known-good baseline',
    flashType: 'FULL FLASH REQUIRED',
    bodyless: true,
  },
];

/**
 * Inspect the datalog to suggest which generator is most relevant.
 * Returns the id of the recommended generator, or null if no strong signal.
 */
function recommend(datalog) {
  if (!datalog) return null;

  const knockEvents = Array.isArray(datalog.knock_events) ? datalog.knock_events.length : 0;
  const wot = datalog.wot_analysis || {};
  const idle = datalog.idle_analysis || {};

  // Knock events / exhaust noise → V22
  if (knockEvents >= 2) return 'v22-exhaust-knock-fix';

  // Rich idle (AFR < 13.5 sustained) → idle lean
  const idleAfr = parseFloat(idle.avg_afr_b1);
  if (!isNaN(idleAfr) && idleAfr > 0 && idleAfr < 13.5) return 'v12-safe-idle-auto';

  // Soft mid-range (low timing 3-4k band) → v12b
  const mid = wot['3000-4000'];
  if (mid && parseFloat(mid.timing) > 0 && parseFloat(mid.timing) < 25) return 'v12b-aggromid';

  return null;
}

export default function GenerateTuneFromDatalog({ datalog, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const recommended = useMemo(() => recommend(datalog), [datalog]);

  const handleGenerate = async (gen) => {
    setSubmitting(gen.id);
    setError(null);
    setResult(null);
    try {
      const token = (() => {
        try { return localStorage.getItem('auth_token'); } catch { return null; }
      })();
      const init = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };
      if (!gen.bodyless && gen.body) init.body = JSON.stringify(gen.body);

      const r = await fetch(`${API_URL}${gen.endpoint}`, init);
      const data = await r.json();
      if (!r.ok) {
        setError(typeof data.detail === 'string' ? data.detail : `HTTP ${r.status}`);
        return;
      }
      setResult({ ...data, _gen: gen });
      if (onSuccess) onSuccess(data);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setSubmitting(null);
    }
  };

  const downloadHref = result?.clean_download_url
    ? `${API_URL}${result.clean_download_url}`
    : null;

  return (
    <>
      <Card className="bg-gradient-to-br from-[#FF5E1A]/10 to-[#111111] border-[#FF5E1A]/30 rounded-sm">
        <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 bg-[#FF5E1A]/20 rounded-sm shrink-0">
              <Sparkles className="w-5 h-5 text-[#FF5E1A]" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-base">Create New Tune from This Datalog</p>
              <p className="text-sm text-zinc-400 mt-0.5">
                {recommended
                  ? `We analyzed this log and recommend a specific generator.`
                  : `Pick a tune generator and we'll build a fresh bin against your latest readings.`}
              </p>
            </div>
          </div>
          <Button
            data-testid="open-generate-tune-from-datalog"
            onClick={() => { setOpen(true); setResult(null); setError(null); }}
            className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm shrink-0"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Create New Tune
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border-[#27272A] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Pick a tune generator</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Each option produces a fresh, checksum-repaired .bin you can download and flash.
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">{result.version || result._gen.name} built successfully</span>
              </div>
              {result.deduped && (
                <div className="text-amber-400 text-sm bg-amber-950/30 border border-amber-500/30 p-3 rounded-sm">
                  ⚠️ Deduped — bytes identical to an existing tune. Using existing bin: {result.filename}
                </div>
              )}
              <dl className="text-sm grid grid-cols-[140px_1fr] gap-y-1 text-zinc-300">
                <dt className="text-zinc-500">Base:</dt><dd className="font-mono">{result.base || '—'}</dd>
                <dt className="text-zinc-500">Bytes changed:</dt><dd>{result.total_bytes_changed_vs_base ?? '—'}</dd>
                <dt className="text-zinc-500">SHA256:</dt><dd className="font-mono text-xs">{(result.sha256 || '').slice(0, 32)}…</dd>
                <dt className="text-zinc-500">Flash type:</dt><dd className="text-amber-300">{result.flash_instructions?.type || result._gen.flashType}</dd>
              </dl>
              {result.warnings && result.warnings.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-amber-300 font-semibold text-sm flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" /> Warnings
                  </p>
                  <ul className="text-xs text-amber-200/80 space-y-1 pl-5 list-disc">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {downloadHref && (
                <a
                  href={downloadHref}
                  data-testid="download-generated-tune"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm font-medium text-sm"
                  download
                >
                  <Download className="w-4 h-4" /> Download {result.clean_filename}
                </a>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400">Close</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              {error && (
                <div className="text-red-400 text-sm bg-red-950/30 border border-red-500/30 p-3 rounded-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {GENERATORS.map((gen) => {
                const isRec = recommended === gen.id;
                const isLoading = submitting === gen.id;
                return (
                  <div
                    key={gen.id}
                    className={`p-4 rounded-sm border ${isRec ? 'border-[#FF5E1A] bg-[#FF5E1A]/5' : 'border-[#27272A] bg-[#111111]'}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="text-white font-semibold text-sm">{gen.name}</h4>
                        {isRec && (
                          <Badge className="bg-[#FF5E1A] text-white text-[10px] px-1.5 py-0">RECOMMENDED</Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">
                        {gen.flashType}
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-400 mb-2">{gen.description}</p>
                    <p className="text-xs text-zinc-500 mb-3">
                      <span className="text-zinc-600">Best for:</span> {gen.bestFor}
                    </p>
                    <Button
                      data-testid={`generate-tune-${gen.id}`}
                      onClick={() => handleGenerate(gen)}
                      disabled={isLoading || submitting !== null}
                      size="sm"
                      className={isRec
                        ? 'bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm'
                        : 'bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm'}
                    >
                      {isLoading ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate {gen.name.split('—')[0].trim()}</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
